<template>
    <div class="payments_page">
        <div class="flex filter_table">
            <div class="flex flex_column w_100">
                <ag-item
                    :detailRowHeight="450"
                    :rowData="rowData"
                    :columnDefs="columnDefs"
                    :masterDetail="true"
                    :detailCellRenderer="detailCellRenderer"
                    :frameworkComponents="frameworkComponents"
                    v-on:sortChanged="sortChanged"
                    :with-tabs="true"
                ></ag-item>
                <Pagination
                    :totalNum="totalNum"
                    :pageSize="pageSize"
                    :sizeChange="sizeChange"
                    :currentChange="currentChange"
                    :currentPage="page"
                ></Pagination>
            </div>
            <PayFilter @apply-filter="applyFilter"/>
        </div>
        <ManualClearingModal ref="clearingModal"></ManualClearingModal>
    </div>
</template>

<script>
import {sellerPaymentsApi} from "@/common/api/sellerPaymentsApi";

import AgItem from "@/components/templates/agGrid/AgItem";
import PayFilter from "./PayFilter";
import {asLocalizedDate, asMoneyAmount, asPercentage} from "@/components/templates/agGrid/formatter/valueFormatters";
import {enumDef} from "@/common/enumDefs";
import ManualClearingModal from "@/components/templates/seller/payments/ManualClearingModal";
import Pagination from "@/components/templates/common/Pagination.vue";
import PaginationParent from "@/components/templates/common/PaginationParent.vue";
import DetailCellRenderer from "@/components/templates/seller/payments/detailCellRenderer";
import CustomHeader from "@/components/templates/agGrid/header/CustomHeader";
import {sellerPaymentsInitiationApi} from "@/common/api/sellerPaymentsInitiationApi";

export default {
    extends: PaginationParent,
    components: { ManualClearingModal, AgItem, PayFilter, Pagination },
    data() {
        return {
            detailCellRenderer: "myDetailCellRenderer",
            frameworkComponents: {
                myDetailCellRenderer: DetailCellRenderer,
				CustomHeader: CustomHeader
            },
            rowData: [],
            columnDefs: [],
            filters: [],
            sorting: [],
        };
    },
    beforeMount() {
        this.setColumnDefs();
        this.getList();
    },
    methods: {
        sortChanged(sortData) {
            this.sorting = sortData;
            this.initPage();
            this.getList();
        },
        getList() {
            let postData = {
                columns: [],
                filters: this.filters || [],
                maxResults: this.maxResults,
                offset: this.offset,
                sorting: this.sorting
            };

            sellerPaymentsApi.inspection
                .list(postData)
                .then(res => {
	                this.setTotalNum(res.recordsTotal);
	                this.rowData = res.data;
	                this.rowData.forEach(row => {
		                if (row.buyerToSellerPaymentStatus !== enumDef.paymentStatus.Revoked) {
			                row.index = ""; // Index has to be set to allow rows to expand
		                }
	                });
                })
                .catch(err => {
                    this.toast(err);
                });
        },
        setColumnDefs() {
          this.columnDefs = [
            {
              field: "index",
              minWidth: 40,
              maxWidth: 40,
              cellRenderer: "agGroupCellRenderer"
            },
            {
              headerName: this.$t("seller.payments.table.header.receivableType"),
              field: "receivableType",
              minWidth: 65,
              maxWidth: 80,
              cellRendererFramework: "EnumAsIcon",
              enumDef: enumDef.receivableType,
              sortable: true
            },
            {
              headerName: this.$t("seller.payments.table.header.invoiceNumber"),
              field: "invoiceNumber",
              minWidth: 150,
              cellRendererFramework: "ItemDetail",
              sortable: true
            },
            {
              headerName: this.$t("common.debtor"),
              field: "debtorName",
              minWidth: 175,
              nameType: "debtorName",
              idType: "debtorId",
              cellRendererFramework: "CompanyTem",
              sortable: true
            },
            {
              headerName: this.$t("common.investor"),
              field: "buyerName",
              minWidth: 175,
              nameType: "buyerName",
              idType: "buyerId",
              cellRendererFramework: "CompanyTem",
              sortable: true
            },
            {
              headerName: this.$t("seller.payments.table.header.dueDate"),
              field: "dueDate",
              minWidth: 120,
              maxWidth: 120,
              valueFormatter: asLocalizedDate(),
              cellRendererFramework: "OverdueRenderer",
              cellRendererParams: {
                auctionStatusField: "auctionStatus",
                paymentStatusField: "sellerToBuyerPaymentStatus"
              },
              sortable: true
            },
            {
              headerName: this.$t("seller.payments.table.header.receivableAmount"),
              headerClass: "align-right",
              field: "amount",
              cellStyle: {'justify-content': 'flex-end'},
              minWidth: 200,
              maxWidth: 250,
              valueFormatter: asMoneyAmount({
                currencyField: "currency"
              }),
              sortable: true
            },
            {
              headerName: this.$t("common.retention"),
              headerClass: "align-right",
              field: "retention",
              cellStyle: {'justify-content': 'flex-end'},
              minWidth: 100,
              valueFormatter: asPercentage({default: "0", decimals: 0 }),
              sortable: true
            },
            {
              headerName: this.$t("common.outstanding"),
              headerComponentParams: {
                tooltip: this.$t("seller.payments.table.header.outstanding.tooltip")
              },
              headerClass: "align-right",
              field: "outStandingAmount",
              cellStyle: {'justify-content': 'flex-end'},
              minWidth: 200,
              maxWidth: 200,
              valueFormatter: asMoneyAmount({
                currencyField: "currency",
                percentValueField: "outStandingPercent"
              }),
              sortable: true
            },
            {
              headerName: this.$t("seller.payments.table.header.progress"),
              minWidth: 210,
              maxWidth: 210,
              cellRendererFramework: "ProgressStatus",
            },
            // Should be enabled later again, when we enable automatic clearing as debtor preset
            /*{
                headerName: this.$t("seller.payments.table.header.automaticClearing"),
                headerComponentParams: {
                    tooltip: this.$t("seller.payments.table.header.automaticClearing.tooltip"),
                },
                field: "automaticClearingPayment",
      cellStyle: {'justify-content': 'center'},
                minWidth: 110,
                maxWidth: 110,
      cellRendererFramework: "BooleanAsIcon"
            },*/
            {
              headerName: this.$t("common.actions"),
              minWidth: 165,
              maxWidth: 165,
              cellRendererFramework: "PaymentTableOperation",
              cellRendererParams: {
                requestClearing: this.requestClearing
              },
              pinned: "right"
            }
          ];
        },
        applyFilter(filters) {
            this.filters = filters;
            this.getList();
        },
		requestClearing(data) {
        	if (data.paymentExecutionType === enumDef.paymentExecutionType.Automatic) {
		        this.showClearingModal(data.sellerToTransferSettlementRecordId);
			} else {
		        this.downloadManualPaymentInfo(data.sellerToTransferSettlementRecordId);
			}
		},
        showClearingModal(recordId) {
            this.$refs.clearingModal.showWithRecord(recordId);
        },
		downloadManualPaymentInfo(recordId) {
			let downloadUrl = sellerPaymentsInitiationApi.inspection.getManualPaymentInfoDownloadLink(recordId);
			window.open(downloadUrl, "_blank");
		}
    }
};
</script>

<style lang="scss" scoped>
@import "./payments.scss";
</style>
